import React, { useState, useEffect } from 'react'
import {
  Box,
  Grid,
  TextField,
  MenuItem,
  Button,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select
} from '@mui/material'
import { LocalizationProvider, DateTimePicker } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { apiGet } from '../../generic/Api_Functions'
import SmartSelect from './../../generic/smartSelect/SmartSelect'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import useFrontEndConfig from '../../generic/hooks/useFrontEndConfig/useFrontEndConfig'
import { formatPartReference } from './partReferenceLabel'
import { NA } from './requestFormNaDefaults'

const ManufacturingRequestForm = ({ formData, setFormData, remoteErrors, onSubmit, isSubmitting }) => {
  const { t_ } = useSmartTranslation()
  // Historia 125 (MIC-4261): the installation decides whether a part reads `name (code)` or
  // `code (name)`. ONE helper paints the selector, resolves the choice and autofills, so the
  // label the user picks is always the label the form can find again (E4).
  const [frontEndConfig] = useFrontEndConfig()
  const partReferenceDisplayFormat = frontEndConfig?.partReferenceDisplayFormat
  const [partReferences, setPartReferences] = useState([])
  // References enriched with their extended columns (the Part Number is extendedColumn12).
  // Served by a dedicated endpoint because partReferencesShortData does not carry extended columns.
  const [extendedPartReferences, setExtendedPartReferences] = useState([])
  const [errors, setErrors] = useState(remoteErrors || {})

  // Story 115: the priority dropdown still starts empty and now offers N/A as a last option.
  // The prototype questionnaire keys this tab does not render stay empty on their own, and the
  // N/A that shows up on the PDF is put there by Sun.

  useEffect(() => {
    const partReferencesFetched = async () => {
      const data = await apiGet('partReferencesShortData')
      const sortedByCode = [...(data || [])].sort((a, b) =>
        (a.code || '').toLowerCase().localeCompare((b.code || '').toLowerCase())
      )
      setPartReferences(sortedByCode)
    }
    partReferencesFetched()
  }, [])

  useEffect(() => {
    const extendedPartReferencesFetched = async () => {
      const data = await apiGet('partReferencesShortDataWithExtendedColumns')
      const sortedByCode = [...(data || [])].sort((a, b) =>
        (a.code || '').toLowerCase().localeCompare((b.code || '').toLowerCase())
      )
      setExtendedPartReferences(sortedByCode)
    }
    extendedPartReferencesFetched()
  }, [])

  useEffect(() => {
    setErrors(remoteErrors || {})
  }, [remoteErrors])

  const handleChange = (e) => {
    const { name, value, files } = e.target
    // Clear error when the user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }))
    }

    if (files) {
      setFormData(prev => ({ ...prev, [name]: files[0] }))
      // Clean file error if one is selected
      if (name === 'imagenComponenteAislado' && files[0]) {
        setErrors(prev => ({ ...prev, [name]: '' }))
      }
      return
    }

    if (name.startsWith('solicitante.')) {
      const key = name.split('.')[1]
      setFormData(prev => ({
        ...prev,
        solicitante: { ...prev.solicitante, [key]: value }
      }))
      // Clear specific error from nested field
      if (errors[name]) {
        setErrors(prev => ({ ...prev, [name]: '' }))
      }
      return
    }

    if (name.startsWith('dimensionesMaximas.')) {
      const key = name.split('.')[1]
      setFormData(prev => ({
        ...prev,
        dimensionesMaximas: { ...prev.dimensionesMaximas, [key]: value }
      }))
      return
    }

    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleDateChange = (date, field) => {
    setFormData(prev => ({ ...prev, [field]: date }))
    // Clear date error if a
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }))
    }
  }

  const handlePartReferenceChange = async (selectedCode) => {
    const selectedPartReference = partReferences.find(m => formatPartReference(m, partReferenceDisplayFormat) === selectedCode)
    if (!selectedPartReference) {
      return
    }
    const fullPartReference = await apiGet('partReferences/' + selectedPartReference.id)
    setFormData(prev => ({
      ...prev,
      partReference: selectedPartReference,
      pieza: selectedCode,
      denominacion: fullPartReference.name,
      materialPropuesto: fullPartReference.materialType,
      // Story 115 (D9): the report prints materialPropuestoCode, a print-only field only Prototype
      // used to fill (handleMaterialChange). Without this, the "Material propuesto" row of the PDF
      // printed N/A over a material that is in fact known - an N/A that lies.
      materialPropuestoCode: fullPartReference.materialType?.code || '',
      piezaCode: selectedCode,
      fore: fullPartReference.extendedColumn1 ? fullPartReference.extendedColumn1 : '',
      sistema: fullPartReference.extendedColumn2 ? fullPartReference.extendedColumn2 : '',
      nocNin: fullPartReference.extendedColumn5 ? fullPartReference.extendedColumn5 : '',
      claseBuque: fullPartReference.extendedColumn6 ? fullPartReference.extendedColumn6 : '',
      nombreBuque: fullPartReference.extendedColumn7 ? fullPartReference.extendedColumn7 : '',
      perteneceCUF: fullPartReference.extendedColumn8 ? fullPartReference.extendedColumn8 : '',
      nocCUF: fullPartReference.extendedColumn9 ? fullPartReference.extendedColumn9 : '',
      denominacionCUF: fullPartReference.extendedColumn10 ? fullPartReference.extendedColumn10 : '',
      claseAvion: fullPartReference.extendedColumn11 ? fullPartReference.extendedColumn11 : '',
      // Keep the Part Number selector in sync with the selected part (unambiguous: one part → one Part Number)
      partNumber: fullPartReference.extendedColumn12 ? fullPartReference.extendedColumn12 : ''

    }))
    // Clear error
    if (errors.pieza) {
      setErrors(prev => ({ ...prev, pieza: '' }))
    }
  }

  const handlePartNumberChange = async (selectedPartNumber) => {
    if (!selectedPartNumber) {
      return
    }
    const matches = extendedPartReferences.filter(r => r.extendedColumn12 === selectedPartNumber)
    // A Part Number can map to several parts: only auto-fill the Piece when the match is unique.
    if (matches.length === 1) {
      await handlePartReferenceChange(formatPartReference(matches[0], partReferenceDisplayFormat))
      return
    }
    // Ambiguous (or no) match: set the Part Number and clear the Piece so the user picks it explicitly.
    setFormData(prev => ({
      ...prev,
      partNumber: selectedPartNumber,
      partReference: {},
      pieza: '',
      piezaCode: ''
    }))
  }

  const SectionTitle = ({ children }) => (
    <Grid item xs={12}>
      <Typography
        variant='h6'
        sx={{
          color: '#7FE1A7',
          borderBottom: '2px solid #444',
          paddingBottom: 1,
          marginBottom: 2
        }}
      >
        {children}
      </Typography>
    </Grid>
  )

  const textFieldStyles = {
    '& .MuiInputBase-input': { color: '#e3e3e3' },
    '& .MuiInputLabel-root': {
      color: '#e3e3e3',
      '&.Mui-error': { color: '#F68D76' }
    },
    '& .MuiOutlinedInput-root': {
      '& fieldset': { borderColor: '#AAAAAA' },
      '&:hover fieldset': { borderColor: '#7FE1A7' },
      '&.Mui-error fieldset': { borderColor: '#F68D76' }
    }
  }

  const selectStyles = {
    color: '#e3e3e3',
    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#AAAAAA' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#7FE1A7' },
    '&.Mui-error .MuiOutlinedInput-notchedOutline': { borderColor: '#F68D76' }
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box
        component='form'
        onSubmit={onSubmit}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
          padding: 3,
          backgroundColor: '#1b1b1b',
          color: '#e3e3e3',
          borderRadius: 2,
          maxWidth: 1000,
          margin: '0 auto'
        }}
      >

        {/* SECCIÓN: IDENTIFICACIÓN */}
        <Paper sx={{ padding: 3, backgroundColor: '#2a2a2a' }}>
          <Grid container spacing={2}>
            <SectionTitle>IDENTIFICACIÓN</SectionTitle>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label='* Elaborado por'
                name='elaboradoPor'
                value={formData.elaboradoPor}
                onChange={handleChange}
                placeholder='Nombre y apellidos del responsable'
                sx={textFieldStyles}
                error={!!errors.elaboradoPor}
                helperText={errors.elaboradoPor}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label='* Codigo proyecto'
                name='codigoProyecto'
                value={formData.codigoProyecto}
                onChange={handleChange}
                sx={textFieldStyles}
                error={!!errors.codigoProyecto}
                helperText={errors.codigoProyecto}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label='Nombre proyecto'
                name='nombreProyecto'
                value={formData.nombreProyecto}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={6}>
              <DateTimePicker
                label='* Fecha objetivo'
                value={formData.fechaObjetivo}
                onChange={(date) => handleDateChange(date, 'fechaObjetivo')}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    sx: textFieldStyles,
                    error: !!errors.fechaObjetivo,
                    helperText: errors.fechaObjetivo,
                    InputLabelProps: {
                      sx: {
                        color: errors.fechaObjetivo ? '#F68D76' : '#e3e3e3'
                      }
                    }
                  }
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: '#e3e3e3' }}>Prioridad</InputLabel>
                <Select
                  name='prioridad'
                  value={formData.prioridad}
                  onChange={handleChange}
                  label='Prioridad'
                  sx={selectStyles}
                >
                  <MenuItem value=''>Seleccionar prioridad...</MenuItem>
                  <MenuItem key={1} value='BAJA'>BAJA</MenuItem>
                  <MenuItem key={2} value='MEDIA'>MEDIA</MenuItem>
                  <MenuItem key={3} value='ALTA'>ALTA</MenuItem>
                  <MenuItem value={NA}>{NA}</MenuItem>

                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label='* Solicitante POC / Empresa - Nombre/apellidos'
                name='solicitanteNombre'
                value={formData.solicitanteNombre}
                onChange={handleChange}
                sx={textFieldStyles}
                error={!!errors.solicitanteNombre}
                helperText={errors.solicitanteNombre}
                inputProps={{ maxLength: 70 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type='email'
                label='Solicitante POC / Empresa - Email'
                name='solicitanteEmail'
                value={formData.solicitanteEmail}
                onChange={handleChange}
                sx={textFieldStyles}
                inputProps={{ maxLength: 70 }}
              />
            </Grid>
            <Grid item xs={8}>
              <SmartSelect
                selectableOptions={partReferences.map((m) => formatPartReference(m, partReferenceDisplayFormat))}
                onChange={handlePartReferenceChange}
                label='Pieza *'
                error={errors.pieza}
                value={formData.pieza}
                sortValues={false}
              />
            </Grid>
            <Grid item xs={4}>
              <SmartSelect
                selectableOptions={[...new Set(extendedPartReferences.map((m) => m.extendedColumn12).filter(Boolean))]}
                onChange={handlePartNumberChange}
                label={t_('Part Number')}
                value={formData.partNumber || ''}
                autoSelectFirst={false}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label='* Nº piezas solicitadas'
                name='numeroPiezasSolicitadas'
                value={formData.numeroPiezasSolicitadas}
                onChange={(e) => {
                  const value = e.target.value
                  if (/^\d*$/.test(value)) {
                    setFormData({
                      ...formData,
                      numeroPiezasSolicitadas: value
                    })
                  }
                }}
                sx={textFieldStyles}
                error={!!errors.numeroPiezasSolicitadas}
                helperText={errors.numeroPiezasSolicitadas}
                inputProps={{
                  inputMode: 'numeric', // mobile numeric keyboard
                  pattern: '[0-9]*'
                }}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* BOTÓN ENVIAR */}
        <Button
          type='submit'
          variant='contained'
          disabled={isSubmitting}
          sx={{
            backgroundColor: '#7FE1A7',
            color: '#000',
            fontWeight: 'bold',
            padding: '12px 24px',
            '&:hover': {
              backgroundColor: '#6bd095'
            }
          }}
        >
          Enviar Solicitud
        </Button>

      </Box>
    </LocalizationProvider>
  )
}

export default ManufacturingRequestForm
